/**
 * Human-readable ID generation for templates and reports
 *
 * Generates stable, readable IDs from Italian labels while handling conflicts,
 * special characters, and evolution over time.
 */

interface IdGenerationOptions {
  prefix: 'sec' | 'fld' | 'row';
  label: string;
  existingIds: Set<string>;
  maxLength?: number; // Default: 40 characters
}

/**
 * Generate a human-readable ID from a label
 *
 * @example
 * generateHumanReadableId({
 *   prefix: 'sec',
 *   label: 'Società',
 *   existingIds: new Set()
 * }) // "sec_societa"
 *
 * @example
 * generateHumanReadableId({
 *   prefix: 'fld',
 *   label: 'Ragione Sociale',
 *   existingIds: new Set()
 * }) // "fld_ragione-sociale"
 */
export function generateHumanReadableId({
  prefix,
  label,
  existingIds,
  maxLength = 40,
}: IdGenerationOptions): string {
  // 1. Slugify the label
  const slug = slugifyItalian(label);

  // 2. Handle empty/whitespace labels
  if (!slug) {
    return generateFallbackId(prefix, existingIds);
  }

  // 3. Truncate to reasonable length (accounting for prefix)
  const maxSlugLength = maxLength - prefix.length - 1; // -1 for underscore
  const truncated = slug.slice(0, maxSlugLength);

  // 4. Build base ID
  const baseId = `${prefix}_${truncated}`;

  // 5. Handle conflicts with incremental suffix
  if (!existingIds.has(baseId)) {
    return baseId;
  }

  // Try numbered variants
  let counter = 2;
  while (existingIds.has(`${baseId}_${counter}`)) {
    counter++;
  }

  return `${baseId}_${counter}`;
}

/**
 * Slugify Italian text to a URL-safe identifier
 *
 * Handles:
 * - Italian accents and diacritics
 * - Italian contractions (dell', l', un', all')
 * - Special characters
 * - Multiple spaces/hyphens
 *
 * @example
 * slugifyItalian("Società") // "societa"
 * slugifyItalian("Ragione Sociale") // "ragione-sociale"
 * slugifyItalian("L'ubicazione dello stabilimento") // "l-ubicazione-dello-stabilimento"
 * slugifyItalian("Periodicità sopralluoghi") // "periodicita-sopralluoghi"
 */
export function slugifyItalian(text: string): string {
  return (
    text
      .toLowerCase()
      .trim()
      // Normalize to NFD (decomposed form) to separate base chars from diacritics
      .normalize('NFD')
      // Remove diacritical marks (accents)
      .replace(/[\u0300-\u036f]/g, '')
      // Handle Italian contractions by replacing apostrophe with hyphen
      .replace(/dell'|l'|un'|all'|dall'|nell'|sull'/g, (match) => match.slice(0, -1) + '-')
      // Replace any non-alphanumeric character with hyphen
      .replace(/[^a-z0-9]+/g, '-')
      // Trim leading/trailing hyphens
      .replace(/^-+|-+$/g, '')
      // Collapse multiple consecutive hyphens
      .replace(/-{2,}/g, '-')
  );
}

/**
 * Generate a generic fallback ID when slugification produces empty string
 */
function generateFallbackId(prefix: 'sec' | 'fld' | 'row', existingIds: Set<string>): string {
  const baseNames = {
    sec: 'sezione',
    fld: 'campo',
    row: 'riga',
  };

  const baseName = baseNames[prefix];
  let counter = 1;

  while (existingIds.has(`${prefix}_${baseName}_${counter}`)) {
    counter++;
  }

  return `${prefix}_${baseName}_${counter}`;
}

/**
 * Build a keypath from section, field, and optional array indices
 *
 * @example
 * buildKeypath("sec_societa", "fld_ragione-sociale")
 * // "sec_societa.fld_ragione-sociale"
 *
 * @example
 * buildKeypath("sec_unita-frigorifere", "fld_capacita-kw", [0])
 * // "sec_unita-frigorifere[0].fld_capacita-kw"
 *
 * @example
 * buildKeypath("sec_unita-frigorifere", "fld_componenti", [0, 2])
 * // "sec_unita-frigorifere[0].fld_componenti[2]"
 */
export function buildKeypath(sectionId: string, fieldId: string, indices?: number[]): string {
  if (!indices || indices.length === 0) {
    return `${sectionId}.${fieldId}`;
  }

  // Build section path with indices
  let path = sectionId;
  for (const index of indices) {
    path += `[${index}]`;
  }

  return `${path}.${fieldId}`;
}

/**
 * Parse a keypath into its components
 *
 * @example
 * parseKeypath("sec_societa.fld_ragione-sociale")
 * // { sectionId: "sec_societa", fieldId: "fld_ragione-sociale", indices: [] }
 *
 * @example
 * parseKeypath("sec_unita-frigorifere[0].fld_capacita-kw")
 * // { sectionId: "sec_unita-frigorifere", fieldId: "fld_capacita-kw", indices: [0] }
 */
export function parseKeypath(keypath: string): {
  sectionId: string;
  fieldId: string;
  indices: number[];
} {
  const parts = keypath.split('.');
  if (parts.length !== 2) {
    throw new Error(`Invalid keypath format: ${keypath}`);
  }

  const [sectionPart, fieldId] = parts;

  // Extract section ID and indices
  const match = sectionPart.match(/^([^[]+)(?:\[(\d+)\])*$/);
  if (!match) {
    throw new Error(`Invalid section part in keypath: ${sectionPart}`);
  }

  const sectionId = match[1];
  const indicesStr = sectionPart.match(/\[(\d+)\]/g);
  const indices = indicesStr ? indicesStr.map((s) => parseInt(s.slice(1, -1), 10)) : [];

  return { sectionId, fieldId, indices };
}

/**
 * Check if a keypath matches a pattern (supports wildcard *)
 *
 * @example
 * matchesKeypath("sec_societa.fld_ragione-sociale", "sec_societa.*")
 * // true
 *
 * @example
 * matchesKeypath("sec_unita-frigorifere[0].fld_capacita-kw", "*.fld_capacita-kw")
 * // true
 */
export function matchesKeypath(keypath: string, pattern: string): boolean {
  const regexPattern = pattern
    .replace(/\./g, '\\.')
    .replace(/\[/g, '\\[')
    .replace(/\]/g, '\\]')
    .replace(/\*/g, '.*');

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(keypath);
}
